import { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";

export default function ContactForm({ selectedDishes = [] }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        date: "",
        location: "",
        message: "",
    });

    const [popupData, setPopupData] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem("bookingBasics");
        if (saved) setPopupData(JSON.parse(saved));
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.email.includes("@")) {
            alert("Please enter a valid email address.");
            return;
        }

        setSending(true);

        const emailData = {
            name: form.name,
            phone: form.phone,
            email: form.email,
            date: form.date,
            location: form.location,
            message: form.message || "No additional message",
            selected_menu: selectedDishes.join(", ") || "No items selected",
            occasion: popupData?.occasion || "N/A",
            service: popupData?.service || "N/A",
            num_people: popupData?.numPeople || "N/A",
        };

        emailjs
            .send("service_bc6qayp", "template_16cdogy", emailData, "RBQf4UwIy7Sv8ZiTd")
            .then(
                () => {
                    alert("🎉 Thank you! Your booking inquiry has been sent successfully.");
                    setForm({
                        name: "",
                        phone: "",
                        email: "",
                        date: "",
                        location: "",
                        message: "",
                    });
                },
                (error) => {
                    console.error("EmailJS Error:", error);
                    alert("⚠️ Failed to send message. Please try again.");
                }
            )
            .finally(() => setSending(false));
    };

    return (
        <section
            id="contact"
            className="bg-white text-[#1F2937] py-20 border-t border-[#D4AF37]/30"
        >
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <h2 className="text-3xl md:text-4xl font-[Playfair_Display] text-[#D4AF37] mb-3 text-center">
                    Share Your Details
                </h2>
                <p className="text-center text-[#4B5563] mb-10 max-w-2xl mx-auto">
                    Fill in your event details and we’ll get in touch soon. <br />
                    <span className="text-[#D4AF37]/90">
                        We cater from 50 to 50,000 guests with complete setup and service.
                    </span>
                </p>

                {/* Form Card */}
                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#F9FAFB] text-[#1F2937]
                     p-10 rounded-2xl border border-[#D4AF37]/60 shadow-lg
                     transition-all"
                >
                    <input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF]
                       focus:border-[#D4AF37] outline-none py-3 transition-all"
                    />

                    <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF]
                       focus:border-[#D4AF37] outline-none py-3 transition-all"
                    />

                    <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF]
                       focus:border-[#D4AF37] outline-none py-3 transition-all"
                    />

                    <input
                        type="date"
                        name="date"
                        placeholder="Event Date"
                        value={form.date}
                        onChange={handleChange}
                        className="bg-transparent border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF]
                       focus:border-[#D4AF37] outline-none py-3 transition-all"
                    />

                    <input
                        type="text"
                        name="location"
                        placeholder="Venue / City"
                        value={form.location}
                        onChange={handleChange}
                        className="bg-transparent border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF]
                       focus:border-[#D4AF37] outline-none py-3 transition-all"
                    />

                    <input
                        type="number"
                        name="numPeople"
                        placeholder="Number of Guests"
                        value={popupData?.numPeople || ""}
                        disabled
                        className="md:col-span-2 bg-[#F3F4F6] border-b border-[#D4AF37]/50 text-[#1F2937] placeholder-[#9CA3AF] outline-none py-3"
                    />

                    <textarea
                        name="message"
                        placeholder="Message or Special Request"
                        value={form.message}
                        onChange={handleChange}
                        className="md:col-span-2 bg-transparent border-b border-[#D4AF37]/50 text-[#fff] placeholder-[#d4af37]/70
                       focus:border-[#D4AF37] outline-none py-3 min-h-[100px] transition-all"
                    />

                    {/* Submit Button */}
                    <div className="md:col-span-2 text-center pt-4">
                        <button
                            type="submit"
                            disabled={sending}
                            className={`px-10 py-3 rounded-full font-semibold tracking-wide transition-all duration-300
                ${
                                sending
                                    ? "bg-[#D4AF37]/60 text-white/60 cursor-not-allowed"
                                    : "bg-gradient-to-r from-[#FFD66E] via-[#D4AF37] to-[#B88A2D] text-white hover:scale-[1.05] shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                            }`}
                        >
                            {sending ? "Sending..." : "Submit Booking Inquiry"}
                        </button>
                    </div>
                </form>

                {/* Selected Dishes */}
                {selectedDishes.length > 0 && (
                    <div className="mt-12">
                        <h3 className="text-xl text-[#D4AF37] font-[Playfair_Display] mb-4 text-center">
                            Selected Dishes ({selectedDishes.length})
                        </h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[#4B5563] text-sm">
                            {selectedDishes.map((dish, i) => (
                                <li
                                    key={i}
                                    className="bg-[#F9FAFB] border border-[#D4AF37]/30
                             rounded-md py-2 px-3 text-center hover:border-[#D4AF37]/60 transition"
                                >
                                    {dish}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
}
